import { BaseLendingPositions, ModeBase } from '../../../types'
import {
  LenderCrossPoolMeta,
  UserDataForSubAccount,
} from '../../user-data/utils/types'

export interface EModeAssets {
  /** marketUids eligible as collateral in this mode */
  collateral: string[]
  /** marketUids eligible for borrowing in this mode */
  borrow: string[]
}

export type EModeBlockReason =
  | 'collateral_not_supported'
  | 'debt_not_supported'
  | 'health_factor'

export interface EModeResult {
  /** Protocol-agnostic mode identifier (replaces legacy `category`) */
  modeId: number
  /** @deprecated Use `modeId` */
  category: number
  label: string
  /** null means no debt (health is infinite) */
  healthFactor: number | null
  supportedAssets: EModeAssets
  /** false if HF <= 1 after switch, user holds incompatible debt, or user has
   *  collateral-enabled assets that are not valid collateral in this mode */
  canSwitch: boolean
  /** Reason the switch is blocked; absent when canSwitch=true */
  blockReason?: EModeBlockReason
  /** marketUids of the positions that block the switch */
  blockingAssets?: string[]
}

/**
 * Compute the hypothetical health factor if the user switches
 * from `currentMode` to `targetMode`.
 *
 * Uses the delta approach: for each position, subtract the old mode's
 * factor contribution and add the target mode's contribution.
 *
 * Returns null when there is no debt (infinite health).
 */
function computeEModeSwitchHealth(
  positions: BaseLendingPositions[],
  lenderMeta: LenderCrossPoolMeta,
  currentCollateral: number,
  currentAdjustedDebt: number,
  currentMode: string,
  targetMode: string,
): number | null {
  if (currentAdjustedDebt === 0) return null
  if (targetMode === currentMode) return currentCollateral / currentAdjustedDebt

  let collateralNew = currentCollateral
  let debtNew = currentAdjustedDebt

  for (let i = 0; i < positions.length; i++) {
    const pos = positions[i]
    const meta = lenderMeta[pos.marketUid]
    if (!meta?.configs) continue
    const configs = meta.configs

    // swap collateral factor contribution (use oracle values for risk accuracy)
    const depositsOracle = pos.depositsUSDOracle ?? pos.depositsUSD
    if (depositsOracle > 0) {
      const oldFactor = configs[currentMode]?.collateralFactor ?? 0
      const newFactor = configs[targetMode]?.collateralFactor ?? 0
      collateralNew += depositsOracle * (newFactor - oldFactor)
    }

    // swap borrow factor contribution
    const totalDebtUSD = (pos.debtUSDOracle ?? pos.debtUSD) + (pos.debtStableUSDOracle ?? pos.debtStableUSD)
    if (totalDebtUSD > 0) {
      const hasTargetConfig = Boolean(configs[targetMode])
      const oldFactor = configs[currentMode]?.borrowFactor ?? 1
      // if target mode has no config for this asset, apply penalty
      const newFactor = hasTargetConfig
        ? (configs[targetMode]?.borrowFactor ?? 1)
        : 1e10
      debtNew += totalDebtUSD * (newFactor - oldFactor)
    }
  }

  const hf = collateralNew / debtNew
  return hf > 0 ? hf : 0
}

/**
 * Get all marketUids that support collateral / borrowing in the given mode.
 */
function getSupportedAssets(
  lenderMeta: LenderCrossPoolMeta,
  targetMode: string,
): EModeAssets {
  const collateral: string[] = []
  const borrow: string[] = []

  for (const [marketUid, meta] of Object.entries(lenderMeta)) {
    const config = meta.configs?.[targetMode]
    if (!config) continue
    if (!config.collateralDisabled) collateral.push(marketUid)
    if (!config.debtDisabled) borrow.push(marketUid)
  }

  return { collateral, borrow }
}

interface SwitchCheck {
  canSwitch: boolean
  blockReason?: EModeBlockReason
  blockingAssets?: string[]
}

/**
 * Returns true when the collateral-enabled asset would be blocked by the
 * `validateSetUserEMode` collateral check introduced in Aave V3.6.
 *
 * Aave V3.6 checks `getUserReserveLtv(asset, targetEMode) != 0` for every
 * collateral-enabled position when switching eModes. LTV = 0 happens when:
 *  - target is eMode 0 (default) and the asset's base LTV is 0
 *  - target eMode N and the asset is NOT in eMode N's collateralBitmap
 *  - target eMode N and the asset IS in collateralBitmap but also in ltvzeroBitmap
 *
 * In the lender metadata this manifests as:
 *  a. `collateralDisabled = true` — set by the v3.6 bitmap parser for eMode > 0
 *     when the asset is absent from the collateralBitmap.
 *  b. `borrowCollateralFactor = 0` — the LTV for this asset in this mode is 0.
 *     For eMode 0: equals the base reserve LTV (0 for ezETH-like assets).
 *     For eMode N: equals the eMode's LTV, which is 0 for ltvzeroBitmap assets.
 *
 * Checking either condition is sufficient to detect a blocked switch.
 *
 * NOTE: older Aave V3 forks (v3.0, v3.2) do NOT have the eMode collateral
 * check; `borrowCollateralFactor = 0` there only means "no borrowing power"
 * but the eMode switch itself is not blocked. For those deployments this
 * check is overly conservative (may predict false negatives), but that is
 * preferable to false positives that lead users into failed transactions.
 */
function isCollateralBlockedInMode(config: any): boolean {
  if (!config) return true
  if (config.collateralDisabled) return true
  if ((config.borrowCollateralFactor ?? 0) === 0) return true
  return false
}

/**
 * Check whether the user can safely switch to `targetMode`.
 *
 * Validates three conditions (in order):
 *  1. Health factor — switching must not drop HF to ≤ 1.
 *  2. Collateral eligibility (Aave V3.6+) — every asset currently enabled as
 *     collateral must be valid collateral in the target mode.
 *  3. Debt eligibility — every borrowed asset must be borrowable in the target
 *     mode (exists in the mode's borrow bitmap / not debtDisabled).
 */
function canSwitchToEMode(
  positions: BaseLendingPositions[],
  lenderMeta: LenderCrossPoolMeta,
  targetMode: string,
  healthFactor: number | null,
): SwitchCheck {
  if (healthFactor !== null && healthFactor <= 1) {
    return { canSwitch: false, blockReason: 'health_factor', blockingAssets: [] }
  }

  const collateralBlocking: string[] = []
  const debtBlocking: string[] = []

  for (let i = 0; i < positions.length; i++) {
    const pos = positions[i]
    const config = lenderMeta[pos.marketUid]?.configs?.[targetMode]

    // Collateral check (Aave V3.6+): if the asset is enabled as collateral,
    // the target mode must support it as collateral with non-zero LTV.
    if (pos.collateralEnabled && pos.depositsUSD > 0) {
      if (isCollateralBlockedInMode(config)) {
        collateralBlocking.push(pos.marketUid)
      }
    }

    // Debt check: every borrowed asset must be borrowable in the target mode.
    const totalDebt = pos.debtUSD + pos.debtStableUSD
    if (totalDebt > 0) {
      if (!config || config.debtDisabled) {
        debtBlocking.push(pos.marketUid)
      }
    }
  }

  // Collateral failures take precedence over debt failures for block reason,
  // but we collect all blocking assets from both categories.
  if (collateralBlocking.length > 0) {
    return {
      canSwitch: false,
      blockReason: 'collateral_not_supported',
      blockingAssets: [...collateralBlocking, ...debtBlocking],
    }
  }

  if (debtBlocking.length > 0) {
    return {
      canSwitch: false,
      blockReason: 'debt_not_supported',
      blockingAssets: debtBlocking,
    }
  }

  return { canSwitch: true }
}

/**
 * For each available e-mode, compute the hypothetical health factor,
 * list supported assets, and determine if the switch is valid.
 */
export function computeEModeAnalysis(
  subAccount: UserDataForSubAccount,
  lenderMeta: LenderCrossPoolMeta,
  eModes: Record<number, ModeBase>,
): EModeResult[] {
  const { positions, balanceData, userConfig } = subAccount
  const currentMode = userConfig.selectedMode

  return Object.values(eModes).map((eMode) => {
    const targetMode = String(eMode.category)
    const healthFactor = computeEModeSwitchHealth(
      positions,
      lenderMeta,
      balanceData.collateral,
      balanceData.adjustedDebt,
      currentMode,
      targetMode,
    )

    const supportedAssets = getSupportedAssets(lenderMeta, targetMode)

    const { canSwitch, blockReason, blockingAssets } = canSwitchToEMode(
      positions,
      lenderMeta,
      targetMode,
      healthFactor,
    )

    return {
      modeId: eMode.category,
      category: eMode.category, // backward-compat alias
      label: eMode.label,
      healthFactor,
      supportedAssets,
      canSwitch,
      ...(blockReason ? { blockReason } : {}),
      ...(blockingAssets ? { blockingAssets } : {}),
    }
  })
}

export const parseHf = (hf: number) => {
  return hf.toLocaleString(undefined, {
    maximumFractionDigits: 2,
    minimumFractionDigits: 2,
  })
}
